// When the app starts use product-data handling.
export default defineNuxtPlugin(() => {
	const appConfig = useAppConfig();
	if (!appConfig?.catalog) return;
	if (!appConfig.catalog.hooks) appConfig.catalog.hooks = {};

	const previousMapProduct = appConfig.catalog.hooks.mapProduct;

	// Run the existing mapProduct first, then variant logic.
	appConfig.catalog.hooks.mapProduct = async (product, targetRoute) => {
		let mapped = product;
		if (typeof previousMapProduct === 'function') {
			const res = await previousMapProduct(product, targetRoute);
			if (res) mapped = res;
		}

		// If response is redirect-only with no product id, then load variant from that redirect.
		if (mapped?.redirect_url_without_domain && !mapped?.id) {
			const resolved = await resolveChildFromRedirect(mapped, appConfig);
			if (resolved) return resolved;
		}

		// Parent configurable is loaded. If the URL has ?search_q show that child variant.
		if (mapped?.id && mapped?.item_type_config?.product_data) {
			const resolved = await resolveChildFromQuery(mapped, appConfig, targetRoute);
			if (resolved) return resolved;
		}

		return mapped;
	};
});

// API returns only a redirect URL with search_q. First load parent then the clicked variant.
async function resolveChildFromRedirect(product, appConfig) {
	try {
		// Path only redirect needs a fake base url so URL() can parse pathname and search_q.
		const parsed = new URL(product.redirect_url_without_domain, 'http://internal');
		const childCode = parsed.searchParams.get('search_q');
		if (!childCode) return null;

		const parent = await fetchParentBySlug(parsed.pathname);
		if (!parent?.id) return null;

		const offer = await fetchChildOffer(parent, childCode, appConfig);
		return offer || parent;
	} catch (e) {
		return null;
	}
}

// If the URL has ?search_q, fetch that child variant.
async function resolveChildFromQuery(product, appConfig, targetRoute) {
	try {
		// During middleware, useRoute() still describes the previous page.
		const route = targetRoute || useRoute();
		let searchQ = route?.query?.search_q;
		if (Array.isArray(searchQ)) searchQ = searchQ[searchQ.length - 1];
		if (!searchQ) return null;
		if (String(product?.code) === String(searchQ)) return null;

		return await fetchChildOffer(product, searchQ, appConfig);
	} catch (e) {
		return null;
	}
}

// Turn the URL slug into product id + slug and fetch that product.
async function fetchParentBySlug(pathname) {
	const cleanPath = String(pathname || '').replace(/\/+$/, '').replace(/^\/+/, '');
	const lastSegment = cleanPath.split('/').pop() || '';
	const parts = lastSegment.split('-');
	if (parts.length < 2) return null;

	const itemId = parts[parts.length - 1];
	// Sometimes the address has one word before the id, sometimes two.
	const sliceEnd = typeof __PRODUCT_URL_TYPE__ !== 'undefined' && __PRODUCT_URL_TYPE__ == 2 ? -1 : -2;
	const itemSlug = parts.slice(0, sliceEnd).join('-');
	if (!itemId || !itemSlug) return null;

	const body = {item_slug: itemSlug, item_id: itemId};
	const res = await useApi(
		'/api/nuxtapi/catalog/product/',
		{method: 'POST', body},
		{cache: true, key: body},
	);
	return res?.data?.id ? res.data : null;
}

// Load the chosen variant full product data from the server.
async function fetchChildOffer(parent, searchCode, appConfig) {
	const child = Object.values(parent?.item_type_config?.product_data || {}).find(
		p => String(p?.code) === String(searchCode),
	);
	if (!child?.offer_shopping_cart_code) return null;

	const endpoints = useEndpoints();
	const baseEndpoint = endpoints.get('_get_hapi_catalog_offer');
	if (!baseEndpoint) return null;

	const checkLists = appConfig?.catalog?.fetch?.check_lists;
	let offerUrl = baseEndpoint.replace('%CODE%', child.offer_shopping_cart_code);
	if (checkLists) offerUrl += `?check_lists=${checkLists}`;

	const res = await useApi(offerUrl, {}, {cache: true, key: offerUrl});
	return res?.data?.id ? res.data : null;
}